/**
 * Branding utilities for applying tenant brand colors to the DOM
 * Extracted from App.tsx for reuse in SchoolConfiguration live preview
 */

import type { Branding } from '@shared/types/tenant-config';
import { generateDangerPalette, isColorDark, neonifyColor } from '@/utils/colorUtils';

/**
 * Luminance threshold below which colors are considered "very dark"
 * and will be transformed for visibility in dark mode.
 */
const DARK_COLOR_LUMINANCE_THRESHOLD = 0.13;

/**
 * Apply branding colors to CSS variables on the document root.
 * Handles dark mode color overrides with fallback to auto-lightening.
 *
 * @param branding - Branding configuration from tenant settings
 */
export function applyBranding(branding: Branding): void {
  const root = document.documentElement;
  const isDarkMode = root.getAttribute('data-theme') === 'dark';

  // Apply primary color
  if (isDarkMode) {
    if (branding.primaryColorDark) {
      // Use explicit dark mode color if set
      root.style.setProperty('--primary-color', branding.primaryColorDark);
    } else if (isColorDark(branding.primaryColor, DARK_COLOR_LUMINANCE_THRESHOLD)) {
      // Auto-transform very dark colors to vibrant neon versions for visibility
      root.style.setProperty('--primary-color', neonifyColor(branding.primaryColor));
    } else {
      root.style.setProperty('--primary-color', branding.primaryColor);
    }
  } else {
    root.style.setProperty('--primary-color', branding.primaryColor);
  }

  // Apply secondary color
  if (isDarkMode) {
    if (branding.secondaryColorDark) {
      // Use explicit dark mode color if set
      root.style.setProperty('--secondary-color', branding.secondaryColorDark);
    } else if (isColorDark(branding.secondaryColor, DARK_COLOR_LUMINANCE_THRESHOLD)) {
      // Auto-transform very dark colors to vibrant neon versions for visibility
      root.style.setProperty('--secondary-color', neonifyColor(branding.secondaryColor));
    } else {
      root.style.setProperty('--secondary-color', branding.secondaryColor);
    }
  } else {
    root.style.setProperty('--secondary-color', branding.secondaryColor);
  }

  // Apply danger color and derived palette
  // In dark mode, use dangerColorDark as base for palette generation if set
  const dangerBase =
    isDarkMode && branding.dangerColorDark ? branding.dangerColorDark : branding.dangerColor;
  const palette = generateDangerPalette(dangerBase);

  // Set error color (uses dark override directly if in dark mode and set)
  if (isDarkMode && branding.dangerColorDark) {
    root.style.setProperty('--error-color', branding.dangerColorDark);
  } else {
    root.style.setProperty('--error-color', palette.errorColor);
  }

  // Apply alert palette based on current theme
  if (isDarkMode) {
    root.style.setProperty('--alert-error-bg', palette.alertErrorBgDark);
    root.style.setProperty('--alert-error-border', palette.alertErrorBorderDark);
    root.style.setProperty('--alert-error-text', palette.alertErrorTextDark);
  } else {
    root.style.setProperty('--alert-error-bg', palette.alertErrorBg);
    root.style.setProperty('--alert-error-border', palette.alertErrorBorder);
    root.style.setProperty('--alert-error-text', palette.alertErrorText);
  }
}

/**
 * Compute the effective dark mode color for a light mode color.
 * Used to show the color picker default when no explicit dark mode color is set.
 *
 * @param lightColor - The light mode hex color
 * @returns The computed dark mode color (neonified if too dark)
 */
export function getEffectiveDarkColor(lightColor: string): string {
  return isColorDark(lightColor, DARK_COLOR_LUMINANCE_THRESHOLD)
    ? neonifyColor(lightColor)
    : lightColor;
}
